import React, { useEffect, useState } from 'react';
import { Download } from '@mui/icons-material';
import { Alert, type AlertDetails, Button, Dialog } from '../../../components';
import {
  downloadFile,
  useGenerateOneOffProfile,
  useGenerateOneOffProfileAssessment,
} from '../../../utils';
import { useAuth } from '../../../contexts';

export type OneOffDialogConfig = {
  open: boolean;
  applicationId: string;
  applicationNo: string;
};

type OneOffDialogProps = {
  openDialogConfig: OneOffDialogConfig;
  onDialogClose: () => void;
};

const DIALOG_TEXT = {
  TITLE: 'Download Profile',
  APPLICATION_PROFILE: 'Application Profile',
  ASSESSMENT_PROFILE: 'Application Assessment Profile',
  HINT: 'Please select the type of file you would like to download.',
  DOWNLOAD_APPLICATION_SUCCESS: 'Downloaded Application Profile Successfully.',
  DOWNLOAD_APPLICATION_FAIL: 'Error downloading application profile:',
  DOWNLOAD_ASSESSMENT_SUCCESS:
    'Downloaded Application Assessment Profile Successfully.',
  DOWNLOAD_ASSESSMENT_FAIL: 'Error downloading application assessment profile:',
  FAIL_TO_FETCH: 'Failed to fetch the file',
};

/**
 * This component would be imported by almost every staff list
 * to download twos types of profile:
 * 1. Application Profile,  generateOneOffFiles as endpoint
 * 2. Assessment Profile, generateOneOffAssessmentProfile as endpoint
 * both of these two endpoints return totalFile and abstract, download totalFile as required.
 */

export const OneOffDialog: React.FC<OneOffDialogProps> = props => {
  const {
    openDialogConfig: { open, applicationId, applicationNo },
    onDialogClose,
  } = props;
  const { handleLoading, pageLoading } = useAuth();

  const [openGPA, setOpenGPA] = useState(open);
  const [alertConfig, setAlertConfig] = useState({
    open: false,
    alertContent: '',
    alertType: 'error',
  });

  const handleClose = (): void => {
    setOpenGPA(false);
    onDialogClose();
  };

  useEffect(() => {
    setOpenGPA(open);
  }, [open]);

  const generateOneOffProfile = useGenerateOneOffProfile();
  const handleDownLoadApplicantOneoff = async (): Promise<void> => {
    await handleLoading(async () => {
      try {
        const result = await generateOneOffProfile({
          variables: { id: applicationId },
        });
        const fileId: string =
          result?.data?.generateOneOffFiles?.totalFile ?? '';
        if (fileId !== '') {
          await downloadFile(
            fileId,
            'pdf',
            applicationNo + '_application_profile',
          );
          setAlertConfig({
            open: true,
            alertContent: DIALOG_TEXT.DOWNLOAD_APPLICATION_SUCCESS,
            alertType: 'success',
          });
        } else {
          throw new Error(DIALOG_TEXT.FAIL_TO_FETCH);
        }
      } catch (error) {
        setAlertConfig({
          open: true,
          alertContent:
            DIALOG_TEXT.DOWNLOAD_APPLICATION_FAIL + (error as Error)?.message,
          alertType: 'warning',
        });
      }
    });
  };

  const generateOneOffProfileAssessment = useGenerateOneOffProfileAssessment();
  const handleDownLoadAssessmentOneoff = async (): Promise<void> => {
    await handleLoading(async () => {
      try {
        const result = await generateOneOffProfileAssessment({
          variables: { id: applicationId },
        });
        const fileId: string =
          result?.data?.generateAssessmentOneOffFiles?.totalFile ?? '';
        if (fileId !== '') {
          await downloadFile(
            fileId,
            'pdf',
            applicationNo + '_assessment_profile',
          );
          setAlertConfig({
            open: true,
            alertContent: DIALOG_TEXT.DOWNLOAD_ASSESSMENT_SUCCESS,
            alertType: 'success',
          });
        } else {
          throw new Error(DIALOG_TEXT.FAIL_TO_FETCH);
        }
      } catch (error) {
        setAlertConfig({
          open: true,
          alertContent:
            DIALOG_TEXT.DOWNLOAD_ASSESSMENT_FAIL + (error as Error)?.message,
          alertType: 'warning',
        });
      }
    });
  };

  const { open: alertOpen, ...alertDetails } = alertConfig;
  return (
    <Dialog
      dialogTitle={DIALOG_TEXT.TITLE}
      openDialog={openGPA}
      displayCloseButton={true}
      onDialogClose={handleClose}
      width="800px"
    >
      <Alert
        openAlert={alertOpen}
        setOpenAlert={() => {}}
        alertDetails={alertDetails as AlertDetails}
      />

      <div className="px-6">
        <div className="flex justify-center w-full py-8 border-solid border-2">
          {DIALOG_TEXT.HINT}
        </div>
      </div>
      <div className="flex flex-row w-full justify-center py-6 gap-8">
        <Button
          onClick={handleDownLoadApplicantOneoff}
          disabled={pageLoading}
          variant="contained"
          size="small"
          sx={{ textTransform: 'none', width: '100%' }}
          startIcon={<Download />}
        >
          {DIALOG_TEXT.APPLICATION_PROFILE}
        </Button>
        <Button
          onClick={handleDownLoadAssessmentOneoff}
          disabled={pageLoading}
          variant="contained"
          size="small"
          sx={{ textTransform: 'none', width: '100%' }}
          startIcon={<Download />}
        >
          {DIALOG_TEXT.ASSESSMENT_PROFILE}
        </Button>
      </div>
    </Dialog>
  );
};
